import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo, faCrosshairs, faFilter, faChevronDown, faChevronRight, faTimes, faTrash, faCopy, faTerminal, faSort, faSortUp, faSortDown, faColumns, faBolt, faShieldAlt, faUnlock, faKey, faCode, faBug, faSitemap, faFileUpload, faBullseye } from '@fortawesome/free-solid-svg-icons';
import httpExchangeService from '../../services/httpExchangeService';
import webVulnScanService from '../../services/webVulnScanService';
import axios from '../../config/axiosConfig';
import { toast } from 'react-toastify';
import ContextMenu from './ContextMenu';
import ScanConfigDialog from './ScanConfigDialog';
import BeaconInjectionDialog from './BeaconInjectionDialog';

const methodBadge = (method, isDarkMode) => {
  const colors = {
    GET: 'text-green-500',
    POST: 'text-yellow-500',
    PUT: 'text-blue-500',
    PATCH: 'text-purple-500',
    DELETE: 'text-red-500'
  };
  return <span className={`font-mono text-xs font-bold ${colors[method] || 'text-gray-400'}`}>{method}</span>;
};

const statusBadge = (code, isDarkMode) => {
  if (!code) return <span className="text-gray-400">-</span>;
  const color = code < 300 ? 'text-green-500' : code < 400 ? 'text-blue-400' : code < 500 ? 'text-yellow-500' : 'text-red-500';
  return <span className={`font-mono text-xs font-bold ${color}`}>{code}</span>;
};

const hasParameters = (ex) => {
  if (ex.request?.queryParams?.length > 0) return true;
  const ct = (ex.request?.headers || []).find(h => h.name?.toLowerCase() === 'content-type')?.value || '';
  if (ct.includes('application/x-www-form-urlencoded') || ct.includes('multipart/form-data')) {
    return !!ex.request?.body;
  }
  return false;
};

const formatDate = (dateStr, compact) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d)) return '-';
  if (compact) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  // Local time (was toISOString = UTC, which showed the wrong hour).
  return d.toLocaleString('sv-SE').slice(0, 19);
};

// Auto-detected attack-surface badges (server sets ex.securityTags).
const SECURITY_BADGES = {
  upload:  { label: 'UPLOAD', title: 'File upload (multipart / attachment endpoint)', bg: '#6366f1' },
  jwt:     { label: 'JWT',    title: 'Contains a JWT (decode/attack in Repeater)',    bg: '#f59e0b' },
  auth:    { label: 'AUTH',   title: 'Authorization header present',                  bg: '#3b82f6' },
  apikey:  { label: 'KEY',    title: 'API key / access token',                        bg: '#14b8a6' },
  graphql: { label: 'GQL',    title: 'GraphQL endpoint',                              bg: '#ec4899' },
  creds:   { label: 'CREDS',  title: 'Credentials in request body',                   bg: '#ef4444' }
};

const DEFAULT_FILTERS = {
  mimeTypes: { html: true, script: true, json: true, xml: true, css: false, images: false, other_text: true, other_binary: true },
  statusCodes: { '2': true, '3': true, '4': true, '5': true },
  search: '',
  searchScope: { url: true, request: false, response: false, headers: false },
  useRegex: false,
  caseSensitive: false,
  negativeSearch: false,
  hideExtensions: 'gif,jpg,png,ico,css,woff,woff2,ttf,svg',
  showExtensions: '',
  excludeUrls: '',
  securityTags: [],
  hasParams: false,
  showOnlyInScope: false
};

const ExchangeTable = ({ webTargetId, projectId, isDarkMode, onSelect, onSendToRepeater, onSendToIntruder, onSendToAuthSmash, filterHost, filterPath, proxyRunning, highlightColors, onHighlightChange, exchangeColors, onExchangeColorChange, onTabChange, onTreeFilterChange }) => {
  const [exchanges, setExchanges] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // Traffic filters persist per web target across tab switches (the tab unmounts
  // when you leave it, so component state alone would reset). Cleared only when the
  // user changes/clears them.
  const filtersStorageKey = `purpleteam-traffic-filters-${webTargetId}`;
  const [sourceFilter, setSourceFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem(filtersStorageKey) || '{}').sourceFilter || ''; } catch { return ''; }
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(filtersStorageKey) || '{}').filters;
      if (saved) return { ...DEFAULT_FILTERS, ...saved };
    } catch { /* ignore */ }
    return DEFAULT_FILTERS;
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Client-side highlight-color filter: 'all' | 'any' | 'none' | '<hex>'.
  // Highlight colors are stored client-side keyed by URL/path, so this filters
  // the currently loaded page only.
  const [colorFilter, setColorFilter] = useState('all');
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [applyToTree, setApplyToTree] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem(filtersStorageKey) || '{}').applyToTree; } catch { return false; }
  });
  const [scanConfigDialog, setScanConfigDialog] = useState(null);
  const [beaconDialog, setBeaconDialog] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [extendedView, setExtendedView] = useState(() => {
    try { return localStorage.getItem('purpleteam-exchange-extended') === 'true'; } catch { return false; }
  });
  const pollRef = useRef(null);
  const prevTotalRef = useRef(0);

  // Persist Traffic filters so they survive leaving/returning to the tab.
  useEffect(() => {
    if (!webTargetId) return;
    try {
      localStorage.setItem(filtersStorageKey, JSON.stringify({ filters, sourceFilter, applyToTree }));
    } catch { /* ignore quota errors */ }
  }, [filters, sourceFilter, applyToTree, filtersStorageKey, webTargetId]);

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') { setSortField('createdAt'); setSortDir('desc'); }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDir === 'asc' ? faSortUp : faSortDown;
  };

  const sortHeaderClass = (field) => {
    const base = 'cursor-pointer select-none hover:text-teal-400 transition-colors';
    if (sortField === field) return `${base} ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`;
    return base;
  };

  const toggleExtendedView = () => {
    setExtendedView(prev => {
      const next = !prev;
      try { localStorage.setItem('purpleteam-exchange-extended', String(next)); } catch {}
      return next;
    });
  };

  const buildFilterParams = useCallback(() => {
    const params = { webTargetId, page, limit: 50 };
    if (filterHost) params.host = filterHost;
    if (filterPath) params.path = filterPath;
    if (sourceFilter) params.source = sourceFilter;

    // Sort params
    if (sortField) {
      params.sortField = sortField;
      params.sortDir = sortDir;
    }

    // Status code filter
    const activeCodes = Object.entries(filters.statusCodes).filter(([, v]) => v).map(([k]) => k);
    if (activeCodes.length > 0 && activeCodes.length < 4) {
      params.statusCodes = activeCodes.join(',');
    }

    // MIME type filter
    const activeMimes = Object.entries(filters.mimeTypes).filter(([, v]) => v).map(([k]) => k);
    if (activeMimes.length > 0 && activeMimes.length < Object.keys(filters.mimeTypes).length) {
      params.mimeTypes = activeMimes.join(',');
    }

    // Search term
    if (filters.search) {
      params.search = filters.search;
      const scope = Object.entries(filters.searchScope || {}).filter(([, v]) => v).map(([k]) => k);
      params.searchScope = (scope.length ? scope : ['url']).join(',');
      if (filters.useRegex) params.useRegex = 'true';
      if (filters.caseSensitive) params.caseSensitive = 'true';
      if (filters.negativeSearch) params.negativeSearch = 'true';
    }

    // File extensions
    if (filters.hideExtensions) params.hideExtensions = filters.hideExtensions;
    if (filters.showExtensions) params.showExtensions = filters.showExtensions;

    // Negative URL filter (hide noise already stored)
    if (filters.excludeUrls) params.excludeUrls = filters.excludeUrls;

    // Attack-surface badge filter (show rows having any selected tag)
    if (filters.securityTags?.length) params.securityTags = filters.securityTags.join(',');

    // Parameterized only
    if (filters.hasParams) params.hasParams = 'true';

    return params;
  }, [webTargetId, page, filterHost, filterPath, sourceFilter, filters, sortField, sortDir]);

  // When "apply to tree" is on, push the current filters to the left site tree in
  // realtime (stripping list-only params: pagination/sort/host/path).
  useEffect(() => {
    if (!onTreeFilterChange) return;
    if (!applyToTree) { onTreeFilterChange(null); return; }
    const { page: _p, sortField: _sf, sortDir: _sd, host: _h, path: _pa, source: _s, ...treeParams } = buildFilterParams();
    onTreeFilterChange(treeParams);
  }, [applyToTree, buildFilterParams, onTreeFilterChange]);

  const loadExchanges = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = buildFilterParams();
      const data = await httpExchangeService.getAll(params);
      setExchanges(data.exchanges || []);
      setTotal(data.total || 0);
      prevTotalRef.current = data.total || 0;
    } catch (err) {
      console.error('Error loading exchanges:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [buildFilterParams]);

  useEffect(() => {
    if (webTargetId) loadExchanges();
  }, [loadExchanges, webTargetId]);

  // Auto-refresh when proxy is running (poll every 3 seconds)
  useEffect(() => {
    if (proxyRunning) {
      pollRef.current = setInterval(() => {
        loadExchanges(true);
      }, 3000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [proxyRunning, loadExchanges]);

  const handleSelect = async (exchange) => {
    setSelectedId(exchange._id);
    try {
      const full = await httpExchangeService.getById(exchange._id);
      onSelect?.(full);
    } catch {}
  };

  const toggleSelectRow = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = visibleExchanges.map(ex => ex._id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  const handleDeleteOne = async (id, e) => {
    e.stopPropagation();
    try {
      await httpExchangeService.remove(id);
      setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      loadExchanges();
    } catch {}
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await httpExchangeService.bulkDeleteByIds([...selectedIds]);
      setSelectedIds(new Set());
      loadExchanges();
    } catch {}
  };

  const handleDeleteAll = async () => {
    if (!webTargetId) return;
    try {
      await httpExchangeService.bulkDelete(webTargetId);
      setSelectedIds(new Set());
      setConfirmDeleteAll(false);
      loadExchanges();
    } catch {}
  };

  // Resolve highlight color for an exchange row. Colors are per-exchange (keyed by
  // _id) so marking one row never tints every other row for the same URL. The site
  // tree keeps its own URL-keyed colors (highlightColors) independently.
  const getRowColor = (ex) => {
    return exchangeColors?.[ex._id] || null;
  };

  // Apply the client-side highlight-color filter to the loaded page.
  const visibleExchanges = colorFilter === 'all'
    ? exchanges
    : exchanges.filter(ex => {
        const col = getRowColor(ex);
        if (colorFilter === 'any') return !!col;
        if (colorFilter === 'none') return !col;
        return col === colorFilter;
      });

  // Handle active vulnerability scan for a specific URL
  const handleActiveScan = useCallback((ex, scanType) => {
    const url = ex.request?.url || '';
    const scope = {
      type: 'url',
      value: url,
      includeChildren: false
    };

    const scanName = `${scanType.toUpperCase()} - ${url}`;

    // Show configuration dialog
    setScanConfigDialog({
      scanName,
      scanType,
      targetScope: scope,
      insertionPoints: ['url_params', 'body_params', 'headers']
    });

    // Close context menu
    setContextMenu(null);
  }, []);

  // Bulk: send all selected traffic rows to the active scanner at once.
  const handleBulkScan = useCallback(() => {
    const urls = [...new Set(
      exchanges
        .filter(ex => selectedIds.has(ex._id))
        .map(ex => ex.request?.url)
        .filter(Boolean)
    )];
    if (urls.length === 0) {
      toast.info('No URLs in the selected rows.');
      return;
    }
    setScanConfigDialog({
      scanName: `Active scan - ${urls.length} URL${urls.length === 1 ? '' : 's'}`,
      scanType: 'all',
      targetScope: { type: 'urls', values: urls, includeChildren: false },
      insertionPoints: ['url_params', 'body_params', 'headers']
    });
  }, [exchanges, selectedIds]);

  const handleStartScan = useCallback(async (config) => {
    try {
      const scan = await webVulnScanService.createScan({
        webTargetId,
        projectId,
        scanType: config.scanType || scanConfigDialog.scanType,
        targetScope: scanConfigDialog.targetScope,
        scanName: config.scanName,
        authProfileId: config.authProfileId || undefined,
        insertionPoints: config.insertionPoints,
        timing: config.timing,
        payloadFilter: config.payloadFilter
      });

      await webVulnScanService.startScan(scan._id);
      toast.success('Vulnerability scan started');

      // Close dialog
      setScanConfigDialog(null);

      // Navigate to scanner tab
      if (onTabChange) {
        onTabChange('scanner');
      }
    } catch (error) {
      console.error('Failed to start scan:', error);
      toast.error('Failed to start vulnerability scan');
    }
  }, [webTargetId, projectId, scanConfigDialog, onTabChange]);

  const handle403Bypass = async (ex) => {
    const url = ex.request?.url;
    if (!url || !webTargetId) return;
    const t = toast.loading('Testing 403/401 bypass techniques…');
    try {
      const { data } = await axios.post('/web-findings/403-bypass', {
        webTargetId, method: ex.request?.method || 'GET', url
      });
      if (!data?.baseline?.denied) {
        toast.update(t, { render: `Baseline is ${data?.baseline?.status} (not 401/403) — nothing to bypass.`, type: 'info', isLoading: false, autoClose: 5000 });
      } else if (data.bypasses?.length) {
        toast.update(t, { render: `⚠️ ${data.bypasses.length} bypass(es) found! Finding created (${data.tested} techniques tried).`, type: 'error', isLoading: false, autoClose: 8000 });
      } else {
        toast.update(t, { render: `No bypass found (${data.tested} techniques tried, baseline ${data.baseline.status}).`, type: 'success', isLoading: false, autoClose: 6000 });
      }
    } catch (err) {
      toast.update(t, { render: 'Bypass test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    }
  };

  const handleCsrfPoc = async (ex) => {
    const t = toast.loading('Generating CSRF PoC…');
    try {
      const { data } = await axios.post('/web-findings/csrf-poc', { exchangeId: ex._id });
      // Open the PoC HTML in a new tab (as a downloadable/viewable blob)
      const blob = new Blob([data.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      try { await navigator.clipboard.writeText(data.html); } catch { /* clipboard may be blocked */ }
      toast.update(t, { render: `CSRF PoC generated (${data.technique}) — opened in a new tab and copied to clipboard.`, type: 'success', isLoading: false, autoClose: 6000 });
    } catch (err) {
      toast.update(t, { render: 'CSRF PoC failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    }
  };

  const handleUploadScan = async (ex) => {
    const t = toast.loading('Testing file-upload restrictions…');
    try {
      const { data } = await axios.post('/web-findings/upload-scan', { exchangeId: ex._id });
      if (data?.note && !data?.findings?.length) {
        toast.update(t, { render: data.note, type: 'info', isLoading: false, autoClose: 6000 });
      } else if (data?.findings?.length) {
        const crit = data.findings.some(f => f.severity === 'critical');
        toast.update(t, { render: `${crit ? '🔴 RCE!' : '⚠️'} Upload bypass: ${data.findings.map(f => f.title).join('; ')}`, type: 'error', isLoading: false, autoClose: 10000 });
      } else {
        const n = (data?.tests || []).length;
        toast.update(t, { render: `No upload bypass detected (${n} variants tried).`, type: 'success', isLoading: false, autoClose: 6000 });
      }
    } catch (err) {
      toast.update(t, { render: 'Upload test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    }
  };

  const handleExtractEndpoints = async (ex) => {
    const t = toast.loading('Extracting endpoints from response…');
    try {
      const { data } = await axios.post('/web-findings/extract-endpoints', { exchangeId: ex._id });
      if (!data?.total) {
        toast.update(t, { render: data?.note || 'No endpoints found in this response.', type: 'info', isLoading: false, autoClose: 5000 });
      } else {
        toast.update(t, { render: `Found ${data.total} endpoint(s)${data.sensitiveCount ? ` (${data.sensitiveCount} sensitive)` : ''} — finding created. See Findings tab.`, type: data.sensitiveCount ? 'warning' : 'success', isLoading: false, autoClose: 7000 });
      }
    } catch (err) {
      toast.update(t, { render: 'Endpoint extraction failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    }
  };

  const handlePrototypePollution = async (ex) => {
    const t = toast.loading('Testing server-side prototype pollution…');
    try {
      const { data } = await axios.post('/web-findings/prototype-pollution', { exchangeId: ex._id });
      if (data?.findings?.length) {
        toast.update(t, { render: `⚠️ Prototype pollution detected: ${data.findings.map(f => f.title).join('; ')}`, type: 'error', isLoading: false, autoClose: 9000 });
      } else if (data?.note) {
        toast.update(t, { render: data.note, type: 'info', isLoading: false, autoClose: 6000 });
      } else {
        const surfaces = (data?.tests || []).map(x => x.surface).join(', ') || 'tests';
        toast.update(t, { render: `No prototype pollution detected (${surfaces}).`, type: 'success', isLoading: false, autoClose: 6000 });
      }
    } catch (err) {
      toast.update(t, { render: 'Prototype pollution test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    }
  };

  const handleSsoAttack = async (ex) => {
    const t = toast.loading('Running SAML/OAuth tampering tests…');
    try {
      const { data } = await axios.post('/web-findings/sso-attack', { exchangeId: ex._id });
      if (data?.note) {
        toast.update(t, { render: data.note, type: 'info', isLoading: false, autoClose: 6000 });
      } else if (data?.findings?.length) {
        toast.update(t, { render: `⚠️ SSO vulnerability found: ${data.findings.map(f => f.title).join('; ')}`, type: 'error', isLoading: false, autoClose: 9000 });
      } else {
        const ran = (data?.tests || []).map(t2 => t2.name).join(', ') || 'tests';
        toast.update(t, { render: `No SSO bypass detected (${ran}).`, type: 'success', isLoading: false, autoClose: 6000 });
      }
    } catch (err) {
      toast.update(t, { render: 'SSO test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    }
  };

  const handleContextMenu = (e, ex) => {
    e.preventDefault();
    e.stopPropagation();
    const buildCurl = (exchange) => {
      const method = exchange.request?.method || 'GET';
      const url = exchange.request?.url || '';
      let cmd = `curl -X ${method} '${url}'`;
      (exchange.request?.headers || []).forEach(h => {
        cmd += ` -H '${h.name}: ${h.value}'`;
      });
      if (exchange.request?.body) cmd += ` -d '${exchange.request.body}'`;
      return cmd;
    };

    setContextMenu({
      position: { x: e.clientX, y: e.clientY },
      items: [
        { label: 'Send to Repeater', icon: faRedo, onClick: () => onSendToRepeater?.(ex) },
        { label: 'Send to Intruder', icon: faCrosshairs, onClick: () => onSendToIntruder?.(ex) },
        { label: 'Send to AuthSmash', icon: faShieldAlt, onClick: () => onSendToAuthSmash?.([ex.request?.url].filter(Boolean)) },
        { label: 'Test 403/401 Bypass', icon: faUnlock, onClick: () => handle403Bypass(ex) },
        { label: 'Test SSO Attacks (SAML/OAuth)', icon: faKey, onClick: () => handleSsoAttack(ex) },
        { label: 'Test Prototype Pollution', icon: faBug, onClick: () => handlePrototypePollution(ex) },
        { label: 'Test File Upload Bypass', icon: faFileUpload, onClick: () => handleUploadScan(ex) },
        { label: 'Beacon Injection', icon: faBullseye, onClick: () => { setContextMenu(null); setBeaconDialog(ex._id); } },
        { label: 'Extract Endpoints (JS/HTML)', icon: faSitemap, onClick: () => handleExtractEndpoints(ex) },
        { label: 'Generate CSRF PoC', icon: faCode, onClick: () => handleCsrfPoc(ex) },
        { divider: true },
        {
          label: 'Scan This URL',
          icon: faBolt,
          submenu: [
            { label: 'All Active Attacks', onClick: () => handleActiveScan(ex, 'all') },
            { divider: true },
            { label: 'XSS (Cross-Site Scripting)', onClick: () => handleActiveScan(ex, 'xss') },
            { label: 'SQL Injection', onClick: () => handleActiveScan(ex, 'sqli') },
            { label: 'CSRF', onClick: () => handleActiveScan(ex, 'csrf') },
            { label: 'SSRF', onClick: () => handleActiveScan(ex, 'ssrf') },
            { label: 'OS Command Injection', onClick: () => handleActiveScan(ex, 'rce') },
            { label: 'Path Traversal', onClick: () => handleActiveScan(ex, 'lfi') },
            { label: 'HTTP Request Smuggling', onClick: () => handleActiveScan(ex, 'http_smuggling') }
          ]
        },
        { divider: true },
        { label: 'Copy URL', icon: faCopy, onClick: () => navigator.clipboard.writeText(ex.request?.url || '') },
        { label: 'Copy as cURL', icon: faTerminal, onClick: async () => {
          try {
            const full = await httpExchangeService.getById(ex._id);
            navigator.clipboard.writeText(buildCurl(full));
          } catch {
            navigator.clipboard.writeText(buildCurl(ex));
          }
        }},
        { divider: true },
        {
          colorPicker: true,
          label: 'Highlight this entry',
          currentColor: exchangeColors?.[ex._id] || null,
          onSelect: (color) => onExchangeColorChange?.(ex._id, color)
        },
        { divider: true },
        { label: 'Delete', icon: faTrash, danger: true, onClick: () => {
          httpExchangeService.remove(ex._id).then(() => loadExchanges()).catch(() => {});
        }}
      ]
    });
  };

  const updateFilter = (key, value) => {
    setFilters(p => ({ ...p, [key]: value }));
    setPage(1);
  };

  const toggleSecurityTag = (tag) => {
    setFilters(p => {
      const cur = p.securityTags || [];
      return { ...p, securityTags: cur.includes(tag) ? cur.filter(t => t !== tag) : [...cur, tag] };
    });
    setPage(1);
  };

  const updateMime = (key, value) => {
    setFilters(p => ({ ...p, mimeTypes: { ...p.mimeTypes, [key]: value } }));
    setPage(1);
  };

  const toggleScope = (key) => {
    setFilters(p => {
      const current = p.searchScope || { url: true };
      const next = { ...current, [key]: !current[key] };
      // Never allow an empty scope — fall back to URL
      if (!next.url && !next.request && !next.response && !next.headers) next.url = true;
      return { ...p, searchScope: next };
    });
    setPage(1);
  };

  const updateStatus = (key, value) => {
    setFilters(p => ({ ...p, statusCodes: { ...p.statusCodes, [key]: value } }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const hasActiveFilters = filters.search || filters.hasParams || filters.showExtensions ||
    Object.values(filters.mimeTypes).some(v => !v) || Object.values(filters.statusCodes).some(v => !v);

  const checkboxClass = `rounded ${isDarkMode ? 'accent-teal-500' : ''}`;
  const filterLabel = `text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      className={`text-left px-3 py-1.5 ${className} ${sortHeaderClass(field)}`}
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        <FontAwesomeIcon icon={sortIcon(field)} className="text-[8px] opacity-60" />
      </span>
    </th>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Filter bar */}
      <div className={`flex items-center gap-2 px-3 py-1.5 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <button
          onClick={() => setShowFilters(p => !p)}
          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
            hasActiveFilters
              ? (isDarkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-50 text-teal-600')
              : (isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500')
          }`}
        >
          <FontAwesomeIcon icon={faFilter} className="text-[10px]" />
          <span>Filter</span>
          <FontAwesomeIcon icon={showFilters ? faChevronDown : faChevronRight} className="text-[8px]" />
        </button>
        <select
          value={sourceFilter}
          onChange={e => { setSourceFilter(e.target.value); setPage(1); }}
          className={`text-xs rounded border px-2 py-0.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          <option value="">All sources</option>
          <option value="proxy">Proxy</option>
          <option value="spider">Spider</option>
          <option value="replay">Repeater</option>
          <option value="intruder">Intruder</option>
          <option value="import">Import</option>
          <option value="authz_test">Auth Test</option>
        </select>
        <button
          onClick={toggleExtendedView}
          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
            extendedView
              ? (isDarkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-50 text-teal-600')
              : (isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500')
          }`}
          title="Toggle extended columns"
        >
          <FontAwesomeIcon icon={faColumns} className="text-[10px]" />
          <span>Extended</span>
        </button>
        {selectedIds.size > 0 && (
          <>
            <button
              onClick={handleBulkScan}
              className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              title="Send the selected URLs to the active scanner"
            >
              <FontAwesomeIcon icon={faBolt} className="text-[10px]" />
              Active Scan ({selectedIds.size})
            </button>
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700"
            >
              <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
              Delete Selected ({selectedIds.size})
            </button>
          </>
        )}
        {total > 0 && (
          confirmDeleteAll ? (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-red-400">Delete all {total}?</span>
              <button onClick={handleDeleteAll} className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white hover:bg-red-700">Yes</button>
              <button onClick={() => setConfirmDeleteAll(false)} className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>No</button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDeleteAll(true)}
              className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded ${isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-500 hover:bg-gray-100'}`}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete All
            </button>
          )
        )}
        {proxyRunning && (
          <span className="flex items-center gap-1 text-[10px] text-green-400">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Live
          </span>
        )}
        <span className={`text-xs ml-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {total} exchanges
        </span>
      </div>

      {/* Expanded filter panel */}
      {showFilters && (
        <div className={`px-3 py-2.5 border-b space-y-2.5 ${isDarkMode ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-100'}`} style={isDarkMode ? { backgroundColor: '#1a1f2e' } : {}}>
          <div className="flex gap-6 flex-wrap">
            {/* MIME type filter */}
            <div>
              <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Filter by MIME type</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                {[
                  ['html', 'HTML'], ['other_text', 'Other text'],
                  ['script', 'Script'], ['images', 'Images'],
                  ['json', 'JSON'], ['other_binary', 'Other binary'],
                  ['xml', 'XML'], ['css', 'CSS']
                ].map(([key, label]) => (
                  <label key={key} className={`flex items-center gap-1.5 cursor-pointer ${filterLabel}`}>
                    <input type="checkbox" checked={filters.mimeTypes[key]} onChange={e => updateMime(key, e.target.checked)} className={checkboxClass} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Status code filter */}
            <div>
              <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Filter by status code</div>
              <div className="space-y-0.5">
                {[
                  ['2', '2xx [success]', 'text-green-500'],
                  ['3', '3xx [redirect]', 'text-blue-400'],
                  ['4', '4xx [client error]', 'text-yellow-500'],
                  ['5', '5xx [server error]', 'text-red-500']
                ].map(([key, label, color]) => (
                  <label key={key} className={`flex items-center gap-1.5 cursor-pointer ${filterLabel}`}>
                    <input type="checkbox" checked={filters.statusCodes[key]} onChange={e => updateStatus(key, e.target.checked)} className={checkboxClass} />
                    <span className={color}>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Request type filter */}
            <div>
              <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Filter by request type</div>
              <div className="space-y-0.5">
                <label className={`flex items-center gap-1.5 cursor-pointer ${filterLabel}`}>
                  <input type="checkbox" checked={filters.hasParams} onChange={e => updateFilter('hasParams', e.target.checked)} className={checkboxClass} />
                  Show only parameterized requests
                </label>
              </div>
            </div>

            {/* File extension filter */}
            <div>
              <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Filter by file extension</div>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <span className={filterLabel}>Hide:</span>
                  <input
                    type="text"
                    value={filters.hideExtensions}
                    onChange={e => updateFilter('hideExtensions', e.target.value)}
                    className={`text-[10px] rounded border px-1.5 py-0.5 w-48 font-mono ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                    placeholder="gif,jpg,png,ico,css"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={filterLabel}>Show only:</span>
                  <input
                    type="text"
                    value={filters.showExtensions}
                    onChange={e => updateFilter('showExtensions', e.target.value)}
                    className={`text-[10px] rounded border px-1.5 py-0.5 w-48 font-mono ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                    placeholder="asp,aspx,jsp,php"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={filterLabel} title="Hide rows whose URL/path matches any of these (comma-separated regex/keywords)">Hide URLs:</span>
                  <input
                    type="text"
                    value={filters.excludeUrls}
                    onChange={e => updateFilter('excludeUrls', e.target.value)}
                    className={`text-[10px] rounded border px-1.5 py-0.5 w-48 font-mono ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                    placeholder="/amb/connect,presence,xmlhttp"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Attack-surface badge filter */}
          <div>
            <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Filter by attack surface (badge)</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {Object.entries(SECURITY_BADGES).map(([tag, meta]) => {
                const active = (filters.securityTags || []).includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => toggleSecurityTag(tag)}
                    title={meta.title}
                    className="px-1.5 py-0.5 rounded text-[9px] font-bold leading-none text-white transition-opacity"
                    style={{ backgroundColor: meta.bg, opacity: active ? 1 : 0.35, outline: active ? '1px solid #fff' : 'none' }}
                  >
                    {meta.label}
                  </button>
                );
              })}
              {(filters.securityTags || []).length > 0 && (
                <button onClick={() => updateFilter('securityTags', [])}
                  className={`text-[9px] px-1.5 py-0.5 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                  clear
                </button>
              )}
            </div>
          </div>

          {/* Search term */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Search term</div>
            <input
              type="text"
              value={filters.search}
              onChange={e => updateFilter('search', e.target.value)}
              className={`text-xs rounded border px-2 py-0.5 w-64 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
              placeholder="Search term (URL / body / headers)..."
            />
            {/* Search scope — where to look. Body search finds sensitive strings
                (usernames, tokens...) even when the endpoint name is unknown. */}
            <div className="flex items-center gap-1">
              <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>in:</span>
              {[
                { key: 'url', label: 'URL' },
                { key: 'request', label: 'Request' },
                { key: 'response', label: 'Response' },
                { key: 'headers', label: 'Headers' }
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => toggleScope(s.key)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                    filters.searchScope?.[s.key]
                      ? 'bg-teal-600 text-white border-teal-600'
                      : isDarkMode ? 'bg-gray-800 text-gray-400 border-gray-600 hover:bg-gray-700' : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-100'
                  }`}
                  title={`Search in ${s.label}${s.key === 'response' ? ' (slower on large captures)' : ''}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <label className={`flex items-center gap-1 cursor-pointer ${filterLabel}`}>
              <input type="checkbox" checked={filters.useRegex} onChange={e => updateFilter('useRegex', e.target.checked)} className={checkboxClass} />
              Regex
            </label>
            <label className={`flex items-center gap-1 cursor-pointer ${filterLabel}`}>
              <input type="checkbox" checked={filters.caseSensitive} onChange={e => updateFilter('caseSensitive', e.target.checked)} className={checkboxClass} />
              Case sensitive
            </label>
            <label className={`flex items-center gap-1 cursor-pointer ${filterLabel}`}>
              <input type="checkbox" checked={filters.negativeSearch} onChange={e => updateFilter('negativeSearch', e.target.checked)} className={checkboxClass} />
              Negative search
            </label>
            <label className={`flex items-center gap-1 cursor-pointer font-medium ${filters.negativeSearch ? '' : ''} ${isDarkMode ? 'text-teal-300' : 'text-teal-600'}`} title="Filter the left site tree by these same filters in realtime">
              <input type="checkbox" checked={applyToTree} onChange={e => setApplyToTree(e.target.checked)} className={checkboxClass} />
              Apply to site tree
            </label>
            {/* Highlight-color filter (client-side, current page). */}
            <div className="flex items-center gap-1.5" title="Filter by right-click highlight color (this page)">
              <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Highlight:</span>
              <button
                onClick={() => setColorFilter('all')}
                className={`text-[10px] px-1.5 py-0.5 rounded ${colorFilter === 'all' ? (isDarkMode ? 'bg-gray-600 text-gray-100' : 'bg-gray-300 text-gray-800') : (isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')}`}
              >All</button>
              <button
                onClick={() => setColorFilter('any')}
                className={`text-[10px] px-1.5 py-0.5 rounded ${colorFilter === 'any' ? (isDarkMode ? 'bg-gray-600 text-gray-100' : 'bg-gray-300 text-gray-800') : (isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')}`}
              >Any</button>
              {['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ec4899'].map(col => (
                <button
                  key={col}
                  onClick={() => setColorFilter(colorFilter === col ? 'all' : col)}
                  className="w-3.5 h-3.5 rounded-sm border"
                  style={{ backgroundColor: col, borderColor: colorFilter === col ? (isDarkMode ? '#fff' : '#111') : 'transparent', outline: colorFilter === col ? '1px solid' : 'none' }}
                  title={`Show only rows highlighted ${col}`}
                />
              ))}
              <button
                onClick={() => setColorFilter('none')}
                className={`text-[10px] px-1.5 py-0.5 rounded ${colorFilter === 'none' ? (isDarkMode ? 'bg-gray-600 text-gray-100' : 'bg-gray-300 text-gray-800') : (isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')}`}
                title="Show only rows without a highlight color"
              >None</button>
            </div>
            {hasActiveFilters && (
              <button onClick={resetFilters} className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 ml-auto">
                <FontAwesomeIcon icon={faTimes} /> Reset filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              <th className="text-center px-1.5 py-1.5 w-8">
                <input
                  type="checkbox"
                  checked={visibleExchanges.length > 0 && visibleExchanges.every(ex => selectedIds.has(ex._id))}
                  onChange={toggleSelectAll}
                  className={checkboxClass}
                />
              </th>
              <SortHeader field="seq" className="w-16">#</SortHeader>
              <SortHeader field="request.method" className="w-16">Method</SortHeader>
              <SortHeader field="request.url">URL</SortHeader>
              <SortHeader field="response.statusCode" className="w-16">Status</SortHeader>
              <SortHeader field="response.contentLength" className="w-20">Length</SortHeader>
              <SortHeader field="response.timing.totalMs" className="w-16">Time</SortHeader>
              {extendedView && (
                <th className={`text-left px-3 py-1.5 w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>MIME</th>
              )}
              <SortHeader field="source" className="w-20">Source</SortHeader>
              <SortHeader field="createdAt" className="w-24">Date</SortHeader>
              {extendedView && (
                <th className={`text-left px-3 py-1.5 w-32 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Notes</th>
              )}
              <th className="text-left px-3 py-1.5 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleExchanges.map((ex, idx) => {
              const rowColor = getRowColor(ex);
              const hasParams = hasParameters(ex);
              return (
              <tr
                key={ex._id}
                onClick={() => handleSelect(ex)}
                onContextMenu={(e) => handleContextMenu(e, ex)}
                className={`cursor-pointer border-b ${
                  selectedId === ex._id
                    ? (isDarkMode ? 'bg-teal-900/20' : 'bg-teal-50')
                    : (isDarkMode ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50')
                }`}
                style={rowColor && selectedId !== ex._id ? { borderLeft: `3px solid ${rowColor}`, backgroundColor: `${rowColor}12` } : {}}
              >
                <td className="text-center px-1.5 py-1">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(ex._id)}
                    onChange={(e) => toggleSelectRow(ex._id, e)}
                    className={checkboxClass}
                  />
                </td>
                <td className={`px-3 py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{ex.seq != null ? ex.seq : (page - 1) * 50 + idx + 1}</td>
                <td className="px-3 py-1">
                  <span className="flex items-center gap-1">
                    {(ex.capturedAnonymous || ex.capturedProfiles?.length > 0) && (
                      <span className="flex items-center gap-0.5 flex-shrink-0">
                        {ex.capturedAnonymous && (
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#9CA3AF' }} title="Anonymous / not logged in" />
                        )}
                        {(ex.capturedProfiles || []).map(p => (
                          <span key={p._id} className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || '#6B7280' }}
                            title={`${p.name}${p.role ? ' — ' + p.role : ''}`} />
                        ))}
                      </span>
                    )}
                    {methodBadge(ex.request?.method, isDarkMode)}
                    {hasParams && (
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" title="Has parameters" />
                    )}
                  </span>
                </td>
                <td className={`px-3 py-1 truncate max-w-md ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="inline-flex items-center gap-1 align-middle">
                    {(ex.securityTags || []).filter(t => SECURITY_BADGES[t]).map(t => (
                      <span key={t} title={SECURITY_BADGES[t].title}
                        className="px-1 py-px rounded text-[8px] font-bold leading-none text-white flex-shrink-0"
                        style={{ backgroundColor: SECURITY_BADGES[t].bg }}>
                        {SECURITY_BADGES[t].label}
                      </span>
                    ))}
                    <span className="truncate">{ex.request?.path || ex.request?.url}</span>
                  </span>
                </td>
                <td className="px-3 py-1">{statusBadge(ex.response?.statusCode, isDarkMode)}</td>
                <td className={`px-3 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {ex.response?.contentLength
                    ? ex.response.contentLength < 1024
                      ? `${ex.response.contentLength}B`
                      : `${(ex.response.contentLength / 1024).toFixed(1)}k`
                    : '-'}
                </td>
                <td className={`px-3 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {ex.response?.timing?.totalMs ? `${ex.response.timing.totalMs}ms` : '-'}
                </td>
                {extendedView && (
                  <td className={`px-3 py-1 truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <span className="font-mono text-[10px]">{ex.response?.contentType?.split(';')[0] || '-'}</span>
                  </td>
                )}
                <td className="px-3 py-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                    {ex.source}
                  </span>
                </td>
                <td className={`px-3 py-1 font-mono text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatDate(ex.createdAt, !extendedView)}
                </td>
                {extendedView && (
                  <td className={`px-3 py-1 truncate text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {ex.notes || '-'}
                  </td>
                )}
                <td className="px-3 py-1">
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); onSendToRepeater?.(ex); }}
                      className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700 text-blue-400' : 'hover:bg-blue-50 text-blue-500'}`}
                      title="Send to Repeater"
                    >
                      <FontAwesomeIcon icon={faRedo} className="text-[10px]" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onSendToIntruder?.(ex); }}
                      className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700 text-orange-400' : 'hover:bg-orange-50 text-orange-500'}`}
                      title="Send to Intruder"
                    >
                      <FontAwesomeIcon icon={faCrosshairs} className="text-[10px]" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteOne(ex._id, e)}
                      className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>

        {exchanges.length === 0 && !loading && (
          <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            No exchanges recorded yet. Start the proxy or import traffic to begin.
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          items={contextMenu.items}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Scan Configuration Dialog */}
      {scanConfigDialog && (
        <ScanConfigDialog
          initialConfig={scanConfigDialog}
          onStart={handleStartScan}
          onCancel={() => setScanConfigDialog(null)}
        />
      )}

      {/* Beacon Injection Dialog */}
      {beaconDialog && (
        <BeaconInjectionDialog
          exchangeId={beaconDialog}
          onClose={() => setBeaconDialog(null)}
        />
      )}

      {/* Pagination */}
      {total > 50 && (
        <div className={`flex items-center justify-between px-3 py-1.5 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700 disabled:opacity-30' : 'text-gray-500 hover:bg-gray-100 disabled:opacity-30'}`}
          >
            Previous
          </button>
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Page {page} of {Math.ceil(total / 50)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 50)}
            className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700 disabled:opacity-30' : 'text-gray-500 hover:bg-gray-100 disabled:opacity-30'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ExchangeTable;
